import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, TextField, MenuItem, Grid,
  Divider, Stack, Chip, IconButton, Tooltip, CircularProgress,
  Alert, Snackbar, Autocomplete,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import PetsIcon from '@mui/icons-material/Pets';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import SearchIcon from '@mui/icons-material/Search';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import HealingIcon from '@mui/icons-material/Healing';
import { useParams, useNavigate } from 'react-router-dom';
import {
  collection, getDocs, addDoc, updateDoc, getDoc,
  doc, serverTimestamp, query, where, orderBy,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';

import { COLORS } from '../theme/constants';

const { primary: PRIMARY, teal: TEAL } = COLORS;

const VISIT_TYPES = [
  { value: 'consulta',   label: 'Consulta general' },
  { value: 'control',    label: 'Control / Seguimiento' },
  { value: 'vacuna',     label: 'Vacunación' },
  { value: 'cirugia',    label: 'Cirugía' },
  { value: 'emergencia', label: 'Emergencia' },
  { value: 'bienestar',  label: 'Bienestar / Preventivo' },
  { value: 'toma_muestra', label: 'Toma de muestra' },
];

const MUCOSAS_OPTIONS = ['Rosa', 'Pálida', 'Ictérica', 'Congestionada', 'Cianótica'];
const TLLC_OPTIONS   = ['< 2 seg', '2–3 seg', '> 3 seg'];
const DIAG_TYPES     = [
  { value: 'definitivo',  label: 'Definitivo' },
  { value: 'presuntivo',  label: 'Presuntivo' },
  { value: 'diferencial', label: 'Diferencial' },
];
const TRAT_TYPES = [
  { value: 'medicamento', label: 'Medicamento' },
  { value: 'procedimiento', label: 'Procedimiento' },
  { value: 'vacuna',      label: 'Vacuna' },
  { value: 'dieta',       label: 'Dieta / Nutrición' },
  { value: 'otro',        label: 'Otro' },
];
const ROUTES = ['Oral', 'IV', 'IM', 'SC', 'Tópico', 'Ocular', 'Ótico', 'Intranasal'];

const emptyTreatment = () => ({ tipo: 'medicamento', descripcion: '', dosis: '', via: '', frecuencia: '', duracion: '' });
const emptyDiagnosis = () => ({ tipo: 'definitivo', descripcion: '', codigo: '', grupo: '' });

function calcAge(bd) {
  if (!bd) return '';
  const y = Math.floor((new Date() - new Date(bd)) / (365.25 * 864e5));
  if (y >= 1) return `${y} año${y !== 1 ? 's' : ''}`;
  const m = Math.floor((new Date() - new Date(bd)) / (30.44 * 864e5));
  return m >= 1 ? `${m} mes${m !== 1 ? 'es' : ''}` : '< 1 mes';
}

// ── Section wrapper ─────────────────────────────────────────────────────────
function Section({ icon, title, color = PRIMARY, children, disabled = false }) {
  return (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #E2E8F0', mb: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Box sx={{ color, fontSize: 22 }}>{icon}</Box>
        <Typography variant="subtitle1" fontWeight={700} color={color}>{title}</Typography>
      </Box>
      <Box component="fieldset" disabled={disabled} sx={{ border: 'none', m: 0, p: 0, ...(disabled && { opacity: 0.5, pointerEvents: 'none' }) }}>
        {children}
      </Box>
    </Paper>
  );
}

// ── Physical exam fields ─────────────────────────────────────────────────────
const PE_FIELDS = [
  { key: 'general',          label: 'Aspecto general' },
  { key: 'integumentario',   label: 'Piel / Pelaje / Anexos' },
  { key: 'ojos',             label: 'Ojos' },
  { key: 'oidosNariz',       label: 'Oídos / Nariz' },
  { key: 'cavidadOral',      label: 'Cavidad oral / Dentición' },
  { key: 'ganglios',         label: 'Ganglios linfáticos' },
  { key: 'cardiorespiratorio', label: 'Cardiorespiratorio' },
  { key: 'abdomen',          label: 'Abdomen / Digestivo' },
  { key: 'locomotor',        label: 'Locomotor / Musculoesquelético' },
  { key: 'neurologico',      label: 'Neurológico' },
  { key: 'reproductor',      label: 'Reproductor / Urogenital' },
];

export default function ClinicalConsultation() {
  const { patientId, consultationId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const isEdit = !!consultationId;
  const isVet = ['médico veterinario', 'director médico'].includes((userData?.role || '').toLowerCase());

  const [patient, setPatient] = useState(null);
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [diagnosesOptions, setDiagnosesOptions] = useState([]);

  // ── Form state ──────────────────────────────────────────────────────────
  const [tipoVisita, setTipoVisita] = useState('');
  const [comentariosTomaMuestra, setComentariosTomaMuestra] = useState('');
  const [vetId, setVetId] = useState('');
  const [vetNombre, setVetNombre] = useState('');

  // S - Subjetivo
  const [motivoConsulta, setMotivoConsulta] = useState('');
  const [anamnesis, setAnamnesis] = useState('');
  const [duracionProblema, setDuracionProblema] = useState('');
  const [cambiosComportamiento, setCambiosComportamiento] = useState('');
  const [alimentacion, setAlimentacion] = useState('');

  // O - Objetivo: Signos Vitales
  const [peso, setPeso] = useState('');
  const [temperatura, setTemperatura] = useState('');
  const [frecuenciaCardiaca, setFrecuenciaCardiaca] = useState('');
  const [frecuenciaRespiratoria, setFrecuenciaRespiratoria] = useState('');
  const [bcs, setBcs] = useState('');
  const [mucosas, setMucosas] = useState('');
  const [tllc, setTllc] = useState('');
  const [pulso, setPulso] = useState('');

  // O - Objetivo: Examen Físico
  const [physicalExam, setPhysicalExam] = useState({});
  const [hallazgosExamen, setHallazgosExamen] = useState('');

  // A - Assessment
  const [diagnosticos, setDiagnosticos] = useState([emptyDiagnosis()]);

  // P - Plan
  const [tratamientos, setTratamientos] = useState([emptyTreatment()]);
  const [instruccionesdueno, setInstruccionesdueno] = useState('');
  const [proximoControl, setProximoControl] = useState('');
  const [notasInternas, setNotasInternas] = useState('');

  // ── Load data ───────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      const [pSnap, profSnap, diagSnap] = await Promise.all([
        getDoc(doc(db, 'patients', patientId)),
        getDocs(query(collection(db, 'professionals'), where('active', '!=', false))),
        getDocs(collection(db, 'diagnoses')),
      ]);
      if (pSnap.exists()) setPatient({ id: pSnap.id, ...pSnap.data() });
      const profs = profSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setProfessionals(profs);
      setDiagnosesOptions(diagSnap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (a.diagnosis || '').localeCompare(b.diagnosis || '', 'es')));

      // Default to the logged-in veterinarian for new records
      if (!isEdit && isVet && userData?.professionalId) {
        const me = profs.find((p) => p.id === userData.professionalId);
        if (me) {
          setVetId(me.id);
          setVetNombre(`Dr/a. ${me.nombre} ${me.apellidos || ''}`.trim());
        }
      }

      if (isEdit) {
        const cSnap = await getDoc(doc(db, 'clinicalRecords', consultationId));
        if (cSnap.exists()) {
          const d = cSnap.data();
          setTipoVisita(d.tipoVisita || '');
          setComentariosTomaMuestra(d.comentariosTomaMuestra || '');
          setVetId(d.vetId || '');
          setVetNombre(d.vetNombre || '');
          setMotivoConsulta(d.motivoConsulta || '');
          setAnamnesis(d.anamnesis || '');
          setDuracionProblema(d.duracionProblema || '');
          setCambiosComportamiento(d.cambiosComportamiento || '');
          setAlimentacion(d.alimentacion || '');
          setPeso(d.peso || '');
          setTemperatura(d.temperatura || '');
          setFrecuenciaCardiaca(d.frecuenciaCardiaca || '');
          setFrecuenciaRespiratoria(d.frecuenciaRespiratoria || '');
          setBcs(d.bcs || '');
          setMucosas(d.mucosas || '');
          setTllc(d.tllc || '');
          setPulso(d.pulso || '');
          setPhysicalExam(d.physicalExam || {});
          setHallazgosExamen(d.hallazgosExamen || '');
          setDiagnosticos(d.diagnosticos?.length > 0 ? d.diagnosticos : [emptyDiagnosis()]);
          setTratamientos(d.tratamientos?.length > 0 ? d.tratamientos : [emptyTreatment()]);
          setInstruccionesdueno(d.instruccionesdueno || '');
          setProximoControl(d.proximoControl || '');
          setNotasInternas(d.notasInternas || '');
        }
      }
      setLoading(false);
    };
    load().catch(() => setLoading(false));
  }, [patientId, consultationId, isEdit]);

  const handleVetChange = (id) => {
    setVetId(id);
    const p = professionals.find((v) => v.id === id);
    setVetNombre(p ? `Dr/a. ${p.nombre} ${p.apellidos || ''}`.trim() : '');
  };

  const setPE = (key) => (e) => setPhysicalExam((prev) => ({ ...prev, [key]: e.target.value }));

  // Diagnoses
  const addDiag = () => setDiagnosticos((p) => [...p, emptyDiagnosis()]);
  const removeDiag = (i) => setDiagnosticos((p) => p.filter((_, idx) => idx !== i));
  const setDiag = (i, field) => (e) => setDiagnosticos((p) => p.map((d, idx) => idx === i ? { ...d, [field]: e.target.value } : d));
  const setDiagFromOption = (i, option) => setDiagnosticos((p) => p.map((d, idx) =>
    idx === i ? { ...d, descripcion: option?.diagnosis || '', codigo: option?.code || '', grupo: option?.group || '' } : d
  ));

  // Treatments
  const addTrat = () => setTratamientos((p) => [...p, emptyTreatment()]);
  const removeTrat = (i) => setTratamientos((p) => p.filter((_, idx) => idx !== i));
  const setTrat = (i, field) => (e) => setTratamientos((p) => p.map((t, idx) => idx === i ? { ...t, [field]: e.target.value } : t));

  const handleSave = async () => {
    if (!tipoVisita) {
      setSnackbar({ open: true, message: 'El tipo de visita es obligatorio.', severity: 'warning' });
      return;
    }
    if (tipoVisita === 'toma_muestra') {
      if (!comentariosTomaMuestra.trim()) {
        setSnackbar({ open: true, message: 'Los comentarios de toma de muestra son obligatorios.', severity: 'warning' });
        return;
      }
    } else {
      if (!motivoConsulta.trim()) {
        setSnackbar({ open: true, message: 'El motivo de consulta es obligatorio.', severity: 'warning' });
        return;
      }
      const diagnosticosValidos = diagnosticos.filter((d) => d.descripcion?.trim());
      if (diagnosticosValidos.length === 0) {
        setSnackbar({ open: true, message: 'Debe registrar al menos un diagnóstico antes de guardar la ficha SOAP.', severity: 'warning' });
        return;
      }
    }
    setSaving(true);
    try {
      const diagnosticosValidos = tipoVisita === 'toma_muestra' ? [] : diagnosticos.filter((d) => d.descripcion?.trim());
      const payload = {
        patientId,
        patientName: patient?.name || '',
        tipoVisita, vetId, vetNombre,
        comentariosTomaMuestra: tipoVisita === 'toma_muestra' ? comentariosTomaMuestra : '',
        motivoConsulta, anamnesis, duracionProblema, cambiosComportamiento, alimentacion,
        peso, temperatura, frecuenciaCardiaca, frecuenciaRespiratoria,
        bcs, mucosas, tllc, pulso,
        physicalExam, hallazgosExamen,
        diagnosticos: diagnosticosValidos,
        tratamientos: tratamientos.filter((t) => t.descripcion.trim()),
        instruccionesdueno, proximoControl, notasInternas,
        updatedAt: serverTimestamp(),
      };

      if (isEdit) {
        await updateDoc(doc(db, 'clinicalRecords', consultationId), payload);
      } else {
        await addDoc(collection(db, 'clinicalRecords'), {
          ...payload,
          createdBy: currentUser?.uid || '',
          createdAt: serverTimestamp(),
        });

        // If there is a booking for this patient/vet/today, mark it as completed
        try {
          const todayStr = new Date().toLocaleDateString('en-CA');
          const q = query(collection(db, 'onlineBookings'), where('petId', '==', patientId));
          const snap = await getDocs(q);
          snap.forEach((b) => {
            const data = b.data();
            if (data.professionalId === vetId && data.date === todayStr && data.status !== 'completed') {
              updateDoc(doc(db, 'onlineBookings', b.id), { status: 'completed', updatedAt: serverTimestamp() });
            }
          });
        } catch (err) { /* ignore booking update errors */ }
      }
      setSnackbar({ open: true, message: `Consulta ${isEdit ? 'actualizada' : 'registrada'} correctamente.`, severity: 'success' });
      setTimeout(() => navigate(`/patients/${patientId}/record`), 1200);
    } catch (e) {
      setSnackbar({ open: true, message: 'Error al guardar: ' + e.message, severity: 'error' });
    }
    setSaving(false);
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>;
  if (!isVet) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
      <Alert severity="error">Sólo los Médicos Veterinarios pueden registrar una consulta SOAP.</Alert>
    </Box>
  );

  const isTomaMuestra = tipoVisita === 'toma_muestra';

  return (
    <Box sx={{ bgcolor: '#F4F6F8', minHeight: '100vh', p: { xs: 1.5, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/patients/${patientId}/record`)}
          sx={{ color: PRIMARY, fontWeight: 600 }}>Volver</Button>
        <MedicalServicesIcon sx={{ color: PRIMARY, fontSize: 28 }} />
        <Box>
          <Typography variant="h5" fontWeight={700} color={PRIMARY}>
            {isEdit ? 'Editar Consulta' : 'Nueva Consulta'} — SOAP
          </Typography>
          {patient && (
            <Typography variant="caption" color="text.secondary">
              {patient.name} · {patient.species} · {calcAge(patient.birthDate)}
            </Typography>
          )}
        </Box>
        <Box flex={1} />
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}
          sx={{ bgcolor: PRIMARY, fontWeight: 700, borderRadius: 2, px: 3 }}>
          {saving ? 'Guardando…' : isEdit ? 'Actualizar' : 'Registrar consulta'}
        </Button>
      </Box>
      {/* Identificación */}
      <Section icon={<PetsIcon />} title="Identificación de la visita">
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
              sm: 4
            }}>
            <TextField select fullWidth size="small" label="Tipo de visita *" required value={tipoVisita} onChange={(e) => setTipoVisita(e.target.value)}>
              <MenuItem value=""><em>Seleccione tipo de visita</em></MenuItem>
              {VISIT_TYPES.map((v) => <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 8
            }}>
            <TextField select fullWidth size="small" label="Médico veterinario *" value={vetId} disabled>
              {professionals.map((p) => (
                <MenuItem key={p.id} value={p.id}>Dr/a. {p.nombre} {p.apellidos || ''}</MenuItem>
              ))}
            </TextField>
          </Grid>
          {isTomaMuestra && (
            <Grid size={12}>
              <TextField
                fullWidth
                size="small"
                label="Comentarios de toma de muestra *"
                required
                multiline
                rows={3}
                placeholder="Registra el tipo de muestra, motivo, condiciones, etc."
                value={comentariosTomaMuestra}
                onChange={(e) => setComentariosTomaMuestra(e.target.value)}
              />
            </Grid>
          )}
        </Grid>
      </Section>
      {/* S — Subjetivo */}
      <Section icon={<SearchIcon />} title="S — Subjetivo (Motivo y Anamnesis)" color="#0369A1" disabled={isTomaMuestra}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField fullWidth size="small" label="Motivo de consulta *" multiline rows={2}
              placeholder="Descripción del problema principal reportado por el propietario"
              value={motivoConsulta} onChange={(e) => setMotivoConsulta(e.target.value)} />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <TextField fullWidth size="small" label="Duración del problema"
              placeholder="Ej: 3 días, 2 semanas, crónico"
              value={duracionProblema} onChange={(e) => setDuracionProblema(e.target.value)} />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <TextField fullWidth size="small" label="Cambios de comportamiento / apetito"
              value={cambiosComportamiento} onChange={(e) => setCambiosComportamiento(e.target.value)} />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <TextField fullWidth size="small" label="Alimentación actual"
              placeholder="Tipo de dieta, frecuencia, apetito"
              value={alimentacion} onChange={(e) => setAlimentacion(e.target.value)} />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            {/* empty column for alignment */}
          </Grid>
          <Grid size={12}>
            <TextField fullWidth size="small" label="Anamnesis completa" multiline rows={3}
              placeholder="Historia clínica detallada: evolución, tratamientos previos, vacunas, cirugías anteriores, antecedentes familiares…"
              value={anamnesis} onChange={(e) => setAnamnesis(e.target.value)} />
          </Grid>
        </Grid>
      </Section>
      {/* O — Signos Vitales */}
      <Section icon={<MonitorHeartIcon />} title="O — Signos Vitales" color="#065F46" disabled={isTomaMuestra}>
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 6,
              sm: 3
            }}>
            <TextField fullWidth size="small" label="Peso (kg)" type="number"
              InputProps={{ inputProps: { step: 0.1, min: 0 } }}
              value={peso} onChange={(e) => setPeso(e.target.value)} />
          </Grid>
          <Grid
            size={{
              xs: 6,
              sm: 3
            }}>
            <TextField fullWidth size="small" label="Temperatura (°C)" type="number"
              InputProps={{ inputProps: { step: 0.1, min: 35, max: 43 } }}
              placeholder="38.0–39.5"
              value={temperatura} onChange={(e) => setTemperatura(e.target.value)} />
          </Grid>
          <Grid
            size={{
              xs: 6,
              sm: 3
            }}>
            <TextField fullWidth size="small" label="Frec. cardíaca (lpm)" type="number"
              value={frecuenciaCardiaca} onChange={(e) => setFrecuenciaCardiaca(e.target.value)} />
          </Grid>
          <Grid
            size={{
              xs: 6,
              sm: 3
            }}>
            <TextField fullWidth size="small" label="Frec. respiratoria (rpm)" type="number"
              value={frecuenciaRespiratoria} onChange={(e) => setFrecuenciaRespiratoria(e.target.value)} />
          </Grid>
          <Grid
            size={{
              xs: 6,
              sm: 3
            }}>
            <TextField fullWidth size="small" label="BCS (1–9)" type="number"
              InputProps={{ inputProps: { min: 1, max: 9 } }}
              value={bcs} onChange={(e) => setBcs(e.target.value)} />
          </Grid>
          <Grid
            size={{
              xs: 6,
              sm: 3
            }}>
            <TextField fullWidth size="small" label="Pulso" value={pulso} onChange={(e) => setPulso(e.target.value)} />
          </Grid>
          <Grid
            size={{
              xs: 6,
              sm: 3
            }}>
            <TextField select fullWidth size="small" label="Mucosas" value={mucosas} onChange={(e) => setMucosas(e.target.value)}>
              <MenuItem value="">—</MenuItem>
              {MUCOSAS_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid
            size={{
              xs: 6,
              sm: 3
            }}>
            <TextField select fullWidth size="small" label="TLLC" value={tllc} onChange={(e) => setTllc(e.target.value)}>
              <MenuItem value="">—</MenuItem>
              {TLLC_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>
      </Section>
      {/* O — Examen Físico */}
      <Section icon={<MedicalServicesIcon />} title="O — Examen Físico por Sistemas" color="#065F46" disabled={isTomaMuestra}>
        <Grid container spacing={2}>
          {PE_FIELDS.map(({ key, label }) => (
            <Grid
              key={key}
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField fullWidth size="small" label={label} multiline maxRows={3}
                placeholder="Normal / Hallazgos"
                value={physicalExam[key] || ''} onChange={setPE(key)} />
            </Grid>
          ))}
          <Grid size={12}>
            <TextField fullWidth size="small" label="Hallazgos relevantes / Resumen del examen" multiline rows={2}
              value={hallazgosExamen} onChange={(e) => setHallazgosExamen(e.target.value)} />
          </Grid>
        </Grid>
      </Section>
      {/* A — Diagnóstico */}
      <Section icon={<SearchIcon />} title="A — Evaluación / Diagnóstico" color="#C2410C" disabled={isTomaMuestra}>
        {diagnosticos.map((d, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 1.5, alignItems: 'flex-start' }}>
            <TextField select size="small" label="Tipo" sx={{ width: 160, flexShrink: 0 }}
              value={d.tipo} onChange={setDiag(i, 'tipo')}>
              {DIAG_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
            <Autocomplete
              fullWidth
              freeSolo
              disabled={isTomaMuestra}
              size="small"
              options={diagnosesOptions}
              getOptionLabel={(opt) => typeof opt === 'string' ? opt : (opt.diagnosis || '')}
              groupBy={(opt) => opt.group || 'Sin grupo'}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              inputValue={d.descripcion}
              onInputChange={(_, val, reason) => {
                if (reason === 'input') setDiag(i, 'descripcion')({ target: { value: val } });
              }}
              onChange={(_, val) => {
                if (val && typeof val === 'object') setDiagFromOption(i, val);
                else if (typeof val === 'string') setDiag(i, 'descripcion')({ target: { value: val } });
              }}
              filterOptions={(opts, { inputValue }) => {
                const q = inputValue.toLowerCase();
                return opts.filter((o) =>
                  o.diagnosis?.toLowerCase().includes(q) ||
                  o.code?.toLowerCase().includes(q) ||
                  o.group?.toLowerCase().includes(q)
                );
              }}
              renderOption={(props, opt) => (
                <Box component="li" {...props} key={opt.id}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{opt.diagnosis}</Typography>
                    <Typography variant="caption" color="text.secondary">{opt.code} · {opt.group}</Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField {...params} size="small" label={`Diagnóstico ${i + 1}`}
                  placeholder="Escribir o buscar por nombre, código o grupo…" />
              )}
            />
            {d.codigo && (
              <Chip label={d.codigo} size="small"
                sx={{ alignSelf: 'center', flexShrink: 0, bgcolor: '#FEF9C3', color: '#854D0E', fontWeight: 700, fontSize: '0.7rem' }} />
            )}
            {diagnosticos.length > 1 && (
              <IconButton size="small" onClick={() => removeDiag(i)} sx={{ color: '#DC2626', alignSelf: 'center' }}>
                <DeleteIcon sx={{ fontSize: 18 }} />
              </IconButton>
            )}
          </Box>
        ))}
        <Button size="small" startIcon={<AddIcon />} onClick={addDiag} sx={{ color: '#C2410C' }}>
          Agregar diagnóstico
        </Button>
      </Section>
      {/* P — Plan */}
      <Section icon={<HealingIcon />} title="P — Plan de Tratamiento" color="#6B21A8" disabled={isTomaMuestra}>
        {tratamientos.map((t, i) => (
          <Paper key={i} elevation={0} sx={{ p: 2, mb: 1.5, bgcolor: '#FAFAFA', borderRadius: 2, border: '1px solid #E9D5FF' }}>
            <Grid container spacing={1.5}>
              <Grid
                size={{
                  xs: 12,
                  sm: 3
                }}>
                <TextField select fullWidth size="small" label="Tipo"
                  value={t.tipo} onChange={setTrat(i, 'tipo')}>
                  {TRAT_TYPES.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 9
                }}>
                <TextField fullWidth size="small" label="Nombre / Descripción *"
                  placeholder="Ej: Amoxicilina, Vacuna antirrábica, Dieta hipoalergénica"
                  value={t.descripcion} onChange={setTrat(i, 'descripcion')} />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  sm: 3
                }}>
                <TextField fullWidth size="small" label="Dosis"
                  placeholder="Ej: 10 mg/kg"
                  value={t.dosis} onChange={setTrat(i, 'dosis')} />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  sm: 3
                }}>
                <TextField select fullWidth size="small" label="Vía"
                  value={t.via} onChange={setTrat(i, 'via')}>
                  <MenuItem value="">—</MenuItem>
                  {ROUTES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  sm: 3
                }}>
                <TextField fullWidth size="small" label="Frecuencia"
                  placeholder="Ej: cada 12h, SID, BID"
                  value={t.frecuencia} onChange={setTrat(i, 'frecuencia')} />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  sm: 3
                }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField fullWidth size="small" label="Duración"
                    placeholder="Ej: 7 días"
                    value={t.duracion} onChange={setTrat(i, 'duracion')} />
                  {tratamientos.length > 1 && (
                    <IconButton size="small" onClick={() => removeTrat(i)} sx={{ color: '#DC2626', alignSelf: 'center' }}>
                      <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Paper>
        ))}
        <Button size="small" startIcon={<AddIcon />} onClick={addTrat} sx={{ color: '#6B21A8', mb: 2 }}>
          Agregar tratamiento
        </Button>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField fullWidth size="small" label="Instrucciones al propietario" multiline rows={3}
              placeholder="Indicaciones para el cuidado en casa, signos de alerta, cuidados post-procedimiento…"
              value={instruccionesdueno} onChange={(e) => setInstruccionesdueno(e.target.value)} />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 4
            }}>
            <TextField fullWidth size="small" label="Fecha próximo control" type="date"
              InputLabelProps={{ shrink: true }}
              value={proximoControl} onChange={(e) => setProximoControl(e.target.value)} />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 8
            }}>
            <TextField fullWidth size="small" label="Notas internas (no visibles al propietario)" multiline rows={2}
              value={notasInternas} onChange={(e) => setNotasInternas(e.target.value)} />
          </Grid>
        </Grid>
      </Section>
      {/* Save button (bottom) */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}
          sx={{ bgcolor: PRIMARY, fontWeight: 700, borderRadius: 2, px: 4, py: 1.2 }}>
          {saving ? 'Guardando…' : isEdit ? 'Actualizar consulta' : 'Registrar consulta'}
        </Button>
      </Box>
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
